<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePolicy } from 'dashboard/composables/usePolicy';
import NewDmModal from './NewDmModal.vue';
import NewGroupModal from './NewGroupModal.vue';
import NewChannelModal from './NewChannelModal.vue';

// Local inline item component to avoid circular imports
import ChatListItem from './ChatListItem.vue';

const emit = defineEmits(['selectChat']);

const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();

const isAdmin = computed(() => checkPermissions(['administrator']));

const showNewDm = ref(false);
const showNewGroup = ref(false);
const showNewChannel = ref(false);
const searchQuery = ref('');

const channels = computed(() => store.getters['internalChats/getChannels']);
const groups = computed(() => store.getters['internalChats/getGroups']);
const dms = computed(() => store.getters['internalChats/getDms']);
const activeChat = computed(() => store.getters['internalChats/getActiveChat']);
const uiFlags = computed(() => store.getters['internalChats/getUIFlags']);
const unreadByChat = computed(
  () => store.getters['internalChats/getUnreadByChat']
);

// DM filter also searches by other_user name (not only chat.name which is null for DMs)
const filterChats = chats => {
  if (!searchQuery.value) return chats;
  const q = searchQuery.value.toLowerCase();
  return chats.filter(c => {
    const name = (c.name || c.other_user?.name || '').toLowerCase();
    const preview = (c.last_message?.content || '').toLowerCase();
    return name.includes(q) || preview.includes(q);
  });
};

const filteredChannels = computed(() => filterChats(channels.value));
const filteredGroups = computed(() => filterChats(groups.value));
const filteredDms = computed(() => filterChats(dms.value));

// Derived empty-state signals
const isLoading = computed(() => uiFlags.value.isFetchingChats);
const noSearchResults = computed(
  () =>
    searchQuery.value &&
    filteredChannels.value.length === 0 &&
    filteredGroups.value.length === 0 &&
    filteredDms.value.length === 0
);

const onSelectChat = chat => {
  emit('selectChat', chat);
};

const onChatCreated = chat => {
  emit('selectChat', chat);
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background border-r border-n-weak">
    <!-- Header -->
    <div class="px-3 pt-3 pb-2 flex items-center justify-between flex-shrink-0">
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('INTERNAL_CHATS.PAGE_TITLE') }}
      </h2>
    </div>

    <!-- Search -->
    <div class="px-3 pb-2 flex-shrink-0">
      <div
        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1.5"
      >
        <span class="i-lucide-search size-3.5 text-n-slate-9 flex-shrink-0" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('INTERNAL_CHATS.SEARCH_CHATS')"
          :aria-label="t('INTERNAL_CHATS.SEARCH_CHATS')"
          class="w-full bg-transparent text-xs text-n-slate-12 outline-none placeholder:text-n-slate-9"
        />
      </div>
    </div>

    <!-- Chat Sections -->
    <nav
      class="flex-1 overflow-y-auto no-scrollbar px-1 pb-3"
      :aria-label="t('INTERNAL_CHATS.PAGE_TITLE')"
    >
      <!-- Loading skeleton -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <span class="i-lucide-loader-2 size-4 text-n-slate-9 animate-spin" />
      </div>

      <template v-else>
        <!-- No search results -->
        <p
          v-if="noSearchResults"
          class="py-6 text-center text-xs text-n-slate-9"
        >
          {{ t('INTERNAL_CHATS.NO_SEARCH_RESULTS') }}
        </p>

        <!-- Channels -->
        <details open class="mb-1">
          <summary
            class="flex cursor-pointer select-none items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-9 hover:text-n-slate-11 list-none"
          >
            <span
              class="i-lucide-chevron-right size-3 transition-transform details-open:rotate-90"
            />
            {{ t('INTERNAL_CHATS.SECTION_CHANNELS') }}
            <button
              v-if="isAdmin"
              class="ml-auto text-n-slate-9 hover:text-n-brand text-xs font-normal normal-case tracking-normal"
              :aria-label="t('INTERNAL_CHATS.NEW_CHANNEL_TITLE')"
              @click.stop="showNewChannel = true"
            >
              {{ t('INTERNAL_CHATS.NEW_CHANNEL') }}
            </button>
          </summary>
          <ul class="mt-0.5 space-y-0.5">
            <li v-for="chat in filteredChannels" :key="chat.id">
              <ChatListItem
                :chat="chat"
                :active="activeChat && activeChat.id === chat.id"
                :unread="unreadByChat[chat.id] || 0"
                @click="onSelectChat(chat)"
              />
            </li>
          </ul>
        </details>

        <!-- Groups -->
        <details open class="mb-1">
          <summary
            class="flex cursor-pointer select-none items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-9 hover:text-n-slate-11 list-none"
          >
            <span
              class="i-lucide-chevron-right size-3 transition-transform details-open:rotate-90"
            />
            {{ t('INTERNAL_CHATS.SECTION_GROUPS') }}
            <button
              v-if="isAdmin"
              class="ml-auto text-n-slate-9 hover:text-n-brand text-xs font-normal normal-case tracking-normal"
              :aria-label="t('INTERNAL_CHATS.NEW_GROUP_TITLE')"
              @click.stop="showNewGroup = true"
            >
              {{ t('INTERNAL_CHATS.NEW_GROUP') }}
            </button>
          </summary>
          <ul class="mt-0.5 space-y-0.5">
            <li v-for="chat in filteredGroups" :key="chat.id">
              <ChatListItem
                :chat="chat"
                :active="activeChat && activeChat.id === chat.id"
                :unread="unreadByChat[chat.id] || 0"
                @click="onSelectChat(chat)"
              />
            </li>
          </ul>
        </details>

        <!-- Direct Messages -->
        <details open class="mb-1">
          <summary
            class="flex cursor-pointer select-none items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-9 hover:text-n-slate-11 list-none"
          >
            <span
              class="i-lucide-chevron-right size-3 transition-transform details-open:rotate-90"
            />
            {{ t('INTERNAL_CHATS.SECTION_DMS') }}
            <button
              class="ml-auto text-n-slate-9 hover:text-n-brand text-xs font-normal normal-case tracking-normal"
              :aria-label="t('INTERNAL_CHATS.NEW_DM')"
              @click.stop="showNewDm = true"
            >
              {{ t('INTERNAL_CHATS.NEW_DM') }}
            </button>
          </summary>
          <ul class="mt-0.5 space-y-0.5">
            <li v-for="chat in filteredDms" :key="chat.id">
              <ChatListItem
                :chat="chat"
                :active="activeChat && activeChat.id === chat.id"
                :unread="unreadByChat[chat.id] || 0"
                @click="onSelectChat(chat)"
              />
            </li>
          </ul>
        </details>
      </template>
    </nav>

    <!-- Modals -->
    <NewDmModal v-model:show="showNewDm" @chat-created="onChatCreated" />
    <NewGroupModal
      v-if="isAdmin"
      v-model:show="showNewGroup"
      @chat-created="onChatCreated"
    />
    <NewChannelModal
      v-if="isAdmin"
      v-model:show="showNewChannel"
      @chat-created="onChatCreated"
    />
  </div>
</template>
